import { ApproachType, Comp, CompsObject } from "../../../endpoints/getExpandedComps";
import { currentYear } from "../../../utils/helperUtils";

export const getSubjectProperty = (data: any, viewType: ApproachType, spaceId?: string) => {
    if (viewType === ApproachType.SALES) {
        return {
            buildingSize: data.appealDetails.buildingSizeSqFt,
            yearBuilt: parseInt(
                data.appealDetails.appealApproachCalculationData.sales.subject.yearBuilt
            ),
        };
    } else if (viewType === ApproachType.INCOME && spaceId) {
        const space = data.appealDetails.appealApproachCalculationData.income.spaces[spaceId];

        return {
            buildingSize: space.leaseAdjustment.subject.buildingSizeSqFt,
            yearBuilt: parseInt(space.leaseAdjustment.subject.yearBuilt),
        };
    }

    return null;
};

export const getSubjectIdentifier = (
    viewType: ApproachType,
    propertyNumber: string,
    spaceId?: string
) => {
    if (viewType === ApproachType.SALES) {
        return propertyNumber;
    } else if (viewType === ApproachType.INCOME) {
        return spaceId;
    }
};

// Filter functions
const matchesSearchQuery = (property: any, searchQuery: string): boolean => {
    if (!searchQuery) return true;
    const searchTerm = searchQuery.toLowerCase();

    return (
        property.address?.toLowerCase().includes(searchTerm) ||
        property.city?.toLowerCase().includes(searchTerm) ||
        property.zip?.toString().includes(searchTerm)
    );
};

const isAddedProperty = (property: any, showAddedOnly: boolean): boolean => {
    return !showAddedOnly || property.enable || property.uiEnabled;
};

const isWithinDistance = (property: any, filterValues: any, subjectProperty: any): boolean => {
    if (filterValues.distance === "All" || !subjectProperty) return true;
    const distanceLimit = parseNumericValue(filterValues.distance);

    return property.distanceFromSource <= distanceLimit;
};

const isWithinSizeRange = (property: any, filterValues: any, subjectProperty: any): boolean => {
    if (filterValues.size === "All" || !subjectProperty) return true;
    const sizePercentage = parseNumericValue(filterValues.size);
    const sizeDiffPercentage = Math.abs(
        ((property.buildingSize - subjectProperty.buildingSize) / subjectProperty.buildingSize) *
            100
    );

    return sizeDiffPercentage <= sizePercentage;
};

const isWithinAgeRange = (property: any, filterValues: any): boolean => {
    if (filterValues.age === "All") return true;
    const ageYears = parseNumericValue(filterValues.age);
    const propertyAge = Math.abs(currentYear - property.yearBuilt);

    return propertyAge <= ageYears;
};

const isWithinAssessmentRange = (
    property: any,
    filterValues: any,
    viewType: ApproachType
): boolean => {
    if (filterValues.assessment === "All") return true;
    const assessmentYears = parseNumericValue(filterValues.assessment);

    let relevantYear: number;
    if (viewType === ApproachType.SALES) {
        relevantYear = property.saleDate
            ? parseInt(property.saleDate.substring(0, 4))
            : currentYear;
    } else {
        relevantYear = property.leaseStartDate
            ? parseInt(property.leaseStartDate.substring(0, 4))
            : currentYear;
    }

    const yearDifference = currentYear - relevantYear;

    return yearDifference <= assessmentYears;
};

// Sorting function
const sortProperties = (
    propertyA: any,
    propertyB: any,
    filterValues: any,
    subjectProperty: any
): number => {
    switch (filterValues.orderBy) {
        case "Distance": {
            return (propertyA.distanceFromSource || 0) - (propertyB.distanceFromSource || 0);
        }
        case "Size difference": {
            if (!subjectProperty) return 0;
            const sizeDiffA = Math.abs(propertyA.buildingSize - subjectProperty.buildingSize);
            const sizeDiffB = Math.abs(propertyB.buildingSize - subjectProperty.buildingSize);

            return sizeDiffA - sizeDiffB;
        }
        case "Age difference": {
            if (!subjectProperty) return 0;
            const ageDiffA = Math.abs(propertyA.yearBuilt - subjectProperty.yearBuilt);
            const ageDiffB = Math.abs(propertyB.yearBuilt - subjectProperty.yearBuilt);

            return ageDiffA - ageDiffB;
        }
        default:
            return 0;
    }
};

export const filterProperties = (
    properties: any[],
    searchQuery: string,
    showAddedOnly: boolean,
    filterValues: any,
    subjectProperty: any,
    viewType: ApproachType
) => {
    return properties
        .filter(
            property =>
                matchesSearchQuery(property, searchQuery) &&
                isAddedProperty(property, showAddedOnly) &&
                isWithinDistance(property, filterValues, subjectProperty) &&
                isWithinSizeRange(property, filterValues, subjectProperty) &&
                isWithinAgeRange(property, filterValues) &&
                isWithinAssessmentRange(property, filterValues, viewType)
        )
        .sort((propertyA, propertyB) =>
            sortProperties(propertyA, propertyB, filterValues, subjectProperty)
        );
};

export const parseNumericValue = (value: string): number => {
    return parseInt(value.replace(/\D/g, ""));
};

export const updateCompsInData = (
    updatedData: any,
    viewType: ApproachType,
    spaceId: string | undefined,
    pendingChanges: any,
    allComps: Comp[]
) => {
    if (viewType === ApproachType.SALES) {
        updateSalesComps(updatedData, pendingChanges, allComps);
    } else if (viewType === ApproachType.INCOME && spaceId) {
        updateIncomeComps(updatedData, spaceId, pendingChanges, allComps);
    }
};

const updateSalesComps = (updatedData: any, pendingChanges: any, allComps: Comp[]) => {
    const salesComps = updatedData.appealDetails.appealApproachCalculationData.sales.comps || {};
    const filteredComps = processComps(salesComps, pendingChanges, allComps);
    updatedData.appealDetails.appealApproachCalculationData.sales.comps = filteredComps;
};

const updateIncomeComps = (
    updatedData: any,
    spaceId: string,
    pendingChanges: any,
    allComps: Comp[]
) => {
    const incomeComps =
        updatedData.appealDetails.appealApproachCalculationData.income.spaces[spaceId]
            .leaseAdjustment.comps || {};
    const filteredComps = processComps(incomeComps, pendingChanges, allComps);
    updatedData.appealDetails.appealApproachCalculationData.income.spaces[
        spaceId
    ].leaseAdjustment.comps = filteredComps;
};

const processComps = (existingComps: CompsObject, pendingChanges: any, allComps: Comp[]) => {
    const filteredComps: CompsObject = {};
    let newIndex = 0;
    const existingRecordIds = new Set<string>();

    // Keep existing comps that weren't removed
    Object.values(existingComps).forEach((comp: Comp) => {
        if (!pendingChanges.removed.has(comp.recordId)) {
            filteredComps[newIndex.toString()] = comp;
            existingRecordIds.add(comp.recordId);
            newIndex++;
        }
    });

    // Add new comps only if they don't already exist
    pendingChanges.added.forEach(recordId => {
        // Skip if this recordId is already in filteredComps
        if (existingRecordIds.has(recordId)) {
            return;
        }

        const comp = allComps.find(c => c.recordId === recordId);
        if (comp) {
            filteredComps[newIndex.toString()] = {
                ...comp,
                uiEnabled: true,
                enable: true,
            };
            newIndex++;
        }
    });

    // Calculate and set equal weights for all comps
    const totalComps = Object.keys(filteredComps).length;
    if (totalComps > 0) {
        const baseWeight = 100 / totalComps;

        Object.keys(filteredComps).forEach(key => {
            filteredComps[key] = {
                ...filteredComps[key],
                weight: Math.ceil(baseWeight * 100) / 100, // Round up to 2 decimal places
            };
        });
    }

    return filteredComps;
};
